import { httpClient } from '../httpClient';

/**
 * DPTA API — ROPA-keyed surface (Spec 031, anchor correction).
 *
 * The DPIA Threshold Assessment is anchored to the register entry
 * (`ROPARecord`) — PDPL Art. 22 / IR Art. 25(1) / IR Art. 33. Every endpoint
 * is keyed on `ropaId`; the per-`ProcessingActivity` surface was retired by
 * the flip.
 */
export const dptaApi = {
  // ── reads — view_dpta ──────────────────────────────────────────────
  // Latest threshold verdict (DPTARecord) for a register entry.
  getByRopaCurrent: (ropaId, { summaryOnly = false } = {}) =>
    httpClient.get(
      `/dpta/by-ropa/${ropaId}/current`,
      summaryOnly ? { params: { summary_only: 'true' } } : undefined,
    ),

  // Bulk current-verdict read for the triage list — one request for the whole
  // cohort instead of an N+1 of per-ROPA `/current` GETs (each 404ing for
  // ROPAs with no verdict yet, which the browser logs as red console errors).
  // Returns only the ROPAs that HAVE a verdict (summary shape).
  getByRopasCurrent: (ropaIds) =>
    httpClient.get('/dpta/by-ropas/current', {
      params: { ropa_ids: (Array.isArray(ropaIds) ? ropaIds : []).join(',') },
    }),

  // Every verdict version for a register entry, newest first.
  getByRopaHistory: (ropaId) =>
    httpClient.get(`/dpta/by-ropa/${ropaId}/history`),

  // The DPO's current declaration set — pre-fills the questionnaire.
  // `declaration` is null when nothing has been declared yet.
  getDeclarationsByRopa: (ropaId) =>
    httpClient.get(`/dpta/by-ropa/${ropaId}/declarations`),

  // ── writes — manage_dpta ───────────────────────────────────────────
  // Re-evaluate the threshold engine for a register entry.
  recomputeByRopa: (ropaId, body) =>
    httpClient.post(`/dpta/by-ropa/${ropaId}/recompute`, body || {}),

  // Persist the DPO questionnaire onto the ROPA's declaration, then
  // recompute. `body` is the camelCase declaration shape; the backend
  // whitelists writable fields. `ifMatch` carries the declaration version
  // last observed — the backend returns 409 VERSION_CONFLICT on a stale
  // write so the hook can refetch instead of clobbering a concurrent edit.
  saveDeclarationsByRopa: (ropaId, body, { ifMatch } = {}) =>
    httpClient.put(
      `/dpta/by-ropa/${ropaId}/declarations`,
      body || {},
      ifMatch != null
        ? { headers: { 'If-Match': String(ifMatch) } }
        : undefined,
    ),

  // ── decision trace + what-if ──────────────────────────────────────
  // Project a verdict for a register entry given flag overrides. An empty
  // `overrides` object yields a pure live-verdict preview.
  whatIfByRopa: (ropaId, overrides) =>
    httpClient.post(`/dpta/by-ropa/${ropaId}/what-if`, { overrides: overrides || {} }),

  getDecisionTraceForRecord: (recordId) =>
    httpClient.get(`/dpta/records/${recordId}/decision-trace`),

  // ── override / sign — manage_dpta + step-up ───────────────────────
  // Backend returns 401 code=step_up_required when password is missing/wrong.
  override: (recordId, { overrideTo, justification, password }) =>
    httpClient.post(`/dpta/records/${recordId}/override`, {
      override_to: overrideTo,
      justification,
      password,
    }),

  // sign_dpta + step-up + SoD — signer must differ from the override actor.
  // `breakGlassReason` is the sanctioned, audited waiver of that SoD block
  // (ADR-019 Option C, `sign_dpta_break_glass` permission) — omitted on the
  // normal path so ordinary sign calls are byte-identical to before.
  sign: (recordId, { password, breakGlassReason } = {}) =>
    httpClient.post(`/dpta/records/${recordId}/sign`, {
      password,
      ...(breakGlassReason ? { breakGlassReason } : {}),
    }),

  getRegistryAudit: (params) =>
    httpClient.get('/dpta/registry/audit', { params }),

  // ── Phase C1 — Adequacy Registry (read-only) ──────────────────────
  // List adequacy rows visible to the caller (global SDAIA baseline +
  // any tenant rows); optional { status } filter. view_dpta. The registry
  // is read-only — overrides are disabled until SDAIA publishes its list.
  listJurisdictionAdequacy: (params) =>
    httpClient.get('/dpta/registry/jurisdiction-adequacy', { params }),

  // ── Spec 031 US3 — chain-reconciliation sweep (view_compliance) ────
  getReconciliationChain: (params) =>
    httpClient.get('/dpta/reconciliation/chain', { params }),

  runReconciliationSweep: () =>
    httpClient.post('/dpta/reconciliation/chain/run'),
};
